// @ts-check

/**
 * Extra seconds to stay below the duration. 0 because the backend rejects only
 * `videoTime > duration`.
 */
export const VIDEO_TIME_END_MARGIN_SEC = 0;

/**
 * @typedef SanitizeVideoTimeOptions
 * @property {number | null} [assetDuration] integer seconds from the server — the bound it validates against
 * @property {number | null} [playerDuration] videojs `player.duration()`: NaN before metadata, Infinity when live
 */

/**
 * @param {unknown} duration
 * @returns {duration is number}
 */
const isUsableDuration = (duration) =>
  typeof duration === 'number' && Number.isFinite(duration) && duration > 0;

/**
 * Turn a raw player time into a `videoTime: Int!` that
 * `updateClassroomSubSectionVideoProgress` will accept — it answers BAD_REQUEST to
 * anything past the duration it stored for the asset.
 *
 * Always returns a non-negative integer (or `null`), and is idempotent, so it is
 * safe to call at both the player and the write site.
 *
 * @param {unknown} rawTime
 * @param {SanitizeVideoTimeOptions} [options]
 * @returns {number | null} the value to send, or `null` to send nothing
 */
const sanitizeVideoTime = (rawTime, { assetDuration, playerDuration } = {}) => {
  if (typeof rawTime !== 'number' || !Number.isFinite(rawTime)) return null;

  // Floor, never round: rounding up past the end of an HLS rendition is what made
  // every finished video report one second too many.
  const flooredTime = Math.max(Math.floor(rawTime), 0);

  const durations = [assetDuration, playerDuration].filter(isUsableDuration);
  if (durations.length === 0) return flooredTime;

  // Smallest bound wins — the HLS media runs slightly longer than the server's integer.
  const cap = Math.max(Math.floor(Math.min(...durations)) - VIDEO_TIME_END_MARGIN_SEC, 0);
  return Math.min(flooredTime, cap);
};

export default sanitizeVideoTime;
